import { type RefObject, useState, useSyncExternalStore } from "react";

type Axis = "x" | "y";

function getVisibleChildren(el: HTMLElement, axis: Axis): HTMLElement[] {
    const prop = axis === "x" ? "offsetWidth" : "offsetHeight";
    return Array.from(el.children).filter(
        (c) => (c as HTMLElement)[prop] > 0,
    ) as HTMLElement[];
}

interface ScrollState {
    activeIndex: number;
    canScrollPrev: boolean;
    canScrollNext: boolean;
}

const INITIAL_STATE: ScrollState = {
    activeIndex: 0,
    canScrollPrev: false,
    canScrollNext: false,
};

export function useSnapScroll(
    scrollRef: RefObject<HTMLElement | null>,
    options?: { axis?: Axis },
) {
    const axis = options?.axis ?? "x";
    const [store] = useState(() => ({
        snapshot: INITIAL_STATE,
        frame: null as number | null,
    }));
    const state = useSyncExternalStore(
        (listener) => {
            const el = scrollRef.current;
            if (!el) return () => {};

            const update = () => {
                const scrollPos = axis === "x" ? el.scrollLeft : el.scrollTop;
                const scrollSize =
                    axis === "x" ? el.scrollWidth : el.scrollHeight;
                const clientSize =
                    axis === "x" ? el.clientWidth : el.clientHeight;

                const center = scrollPos + clientSize / 2;
                const children = getVisibleChildren(el, axis);
                let closestIdx = 0;
                let closestDist = Infinity;
                for (let i = 0; i < children.length; i++) {
                    const child = children[i];
                    const childOffset =
                        axis === "x" ? child.offsetLeft : child.offsetTop;
                    const childSize =
                        axis === "x" ? child.offsetWidth : child.offsetHeight;
                    const childCenter = childOffset + childSize / 2;
                    const dist = Math.abs(center - childCenter);
                    if (dist < closestDist) {
                        closestDist = dist;
                        closestIdx = i;
                    }
                }

                const snapshot = {
                    activeIndex: closestIdx,
                    canScrollPrev: scrollPos > 2,
                    canScrollNext: scrollPos < scrollSize - clientSize - 2,
                };
                if (
                    snapshot.activeIndex === store.snapshot.activeIndex &&
                    snapshot.canScrollPrev === store.snapshot.canScrollPrev &&
                    snapshot.canScrollNext === store.snapshot.canScrollNext
                )
                    return;
                store.snapshot = snapshot;
                listener();
            };

            const onScroll = () => {
                if (store.frame !== null) return;
                store.frame = requestAnimationFrame(() => {
                    store.frame = null;
                    update();
                });
            };

            update();
            el.addEventListener("scroll", onScroll, { passive: true });
            el.addEventListener("scrollend", update);
            const ro = new ResizeObserver(update);
            ro.observe(el);

            return () => {
                el.removeEventListener("scroll", onScroll);
                el.removeEventListener("scrollend", update);
                ro.disconnect();
                if (store.frame !== null) cancelAnimationFrame(store.frame);
            };
        },
        () => store.snapshot,
        () => INITIAL_STATE,
    );

    const scrollTo = (index: number) => {
        const el = scrollRef.current;
        if (!el) return;
        const children = getVisibleChildren(el, axis);
        const child = children[index];
        if (!child) return;
        el.scrollTo({
            [axis === "x" ? "left" : "top"]:
                axis === "x" ? child.offsetLeft : child.offsetTop,
            behavior: "smooth",
        });
    };

    const scrollPrev = () => scrollTo(Math.max(0, state.activeIndex - 1));
    const scrollNext = () => {
        const el = scrollRef.current;
        const max = el ? getVisibleChildren(el, axis).length - 1 : 0;
        scrollTo(Math.min(max, state.activeIndex + 1));
    };

    return {
        activeIndex: state.activeIndex,
        canScrollPrev: state.canScrollPrev,
        canScrollNext: state.canScrollNext,
        scrollTo,
        scrollPrev,
        scrollNext,
    };
}
